/**
 * Main container for financial information with revenue, expenses, and profit
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  Alert,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import type { HoldList, Hold, Show } from 'api-client/models';
import { formatDateLong, isDateTodayOrPast } from '@/utils/dateUtils';
import { TicketRevenueSection } from './TicketRevenueSection';
import { OtherRevenueSection } from './OtherRevenueSection';
import { ExpenseSection } from './ExpenseSection';
import { ProfitSummary } from './ProfitSummary';
import { InlineEditingControls } from '../forms/InlineEditingControls';

interface FinancialSummaryCardProps {
  hold: HoldList | null;
  holdDetails: Hold | null;
  show: Show;
  isEditing: boolean;
  formData: Record<string, unknown>;
  onStartEdit: (section: string, holdId: string) => Promise<void>;
  onSave: (section: string) => Promise<void>;
  onCancel: () => void;
  onUpdateField: (field: string, value: unknown) => void;
  onCopyExpenses?: () => Promise<void>;
  otherConfirmedHoldCount: number;
  userTimezone: string;
}

export const FinancialSummaryCard: React.FC<FinancialSummaryCardProps> = ({
  hold,
  holdDetails,
  show,
  isEditing,
  formData,
  onStartEdit,
  onSave,
  onCancel,
  onUpdateField,
  onCopyExpenses,
  otherConfirmedHoldCount,
  userTimezone,
}) => {
  if (!hold) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Financial Summary
          </Typography>
          <Alert severity="info">Select a date above to view financial information.</Alert>
        </CardContent>
      </Card>
    );
  }

  // Check if date is today or in the past in user timezone.
  const isDatePast = isDateTodayOrPast(hold.holdDate, userTimezone);

  return (
    <Card>
      <CardContent>
        <Stack direction="row" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }} alignItems="center">
            <MoneyIcon />
            <Typography variant="h6" component="span">
              Financial Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" component="span">
              - {formatDateLong(hold.holdDate)}
            </Typography>
          </Stack>
          {!isEditing && show.status === 'confirmed' && (
            <IconButton
              size="small"
              onClick={() => void onStartEdit('financial', hold.id)}
              color="primary"
              aria-label="Edit financial details"
            >
              <EditIcon />
            </IconButton>
          )}
        </Stack>

        {!isDatePast && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This show date is in the future. Advance ticket sales and expenses can be recorded now;
            attendance, bar revenue, and other revenue are entered after the show.
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Ticket Revenue Section */}
          <TicketRevenueSection
            ticketRevenueItems={holdDetails?.ticketRevenueItems || []}
            totalTicketRevenue={holdDetails?.ticketRevenue ? parseFloat(String(holdDetails.ticketRevenue)) : 0}
            isEditing={isEditing}
            isDatePast={isDatePast}
            formData={formData}
            onUpdateField={onUpdateField}
            onStartEdit={onStartEdit}
            holdId={hold.id}
          />

          {/* Other Revenue Section */}
          <OtherRevenueSection
            barRevenue={holdDetails?.barRevenue ? parseFloat(String(holdDetails.barRevenue)) : 0}
            otherRevenue={holdDetails?.otherRevenue ? parseFloat(String(holdDetails.otherRevenue)) : 0}
            isEditing={isEditing}
            isDatePast={isDatePast}
            formData={formData}
            onUpdateField={onUpdateField}
            onStartEdit={onStartEdit}
            holdId={hold.id}
          />

          {/* Expenses Section */}
          <ExpenseSection
            expenseItems={holdDetails?.expenseItems || []}
            totalExpenses={holdDetails?.totalExpenses ? parseFloat(String(holdDetails.totalExpenses)) : 0}
            isEditing={isEditing}
            formData={formData}
            onUpdateField={onUpdateField}
            onCopyExpenses={onCopyExpenses}
            showCopyButton={otherConfirmedHoldCount > 0}
            onStartEdit={onStartEdit}
            holdId={hold.id}
          />

          {/* Profit Summary - Only in view mode */}
          {!isEditing && (
            <ProfitSummary
              netProfit={holdDetails?.netProfit ? parseFloat(String(holdDetails.netProfit)) : 0}
              isSettled={hold.isSettled || false}
              onStartEdit={async () => {
                await onStartEdit('financial', hold.id);
              }}
            />
          )}

          {/* Edit Mode Actions */}
          {isEditing && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <InlineEditingControls onSave={() => onSave('financial')} onCancel={onCancel}
                fullWidth saveLabel="SAVE" cancelLabel="CANCEL" />
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
